<script setup lang="ts">
/**
 * PaperCutout — one of the hand-drawn illustrations as a paper cut-out (cream
 * scissor-cut margin baked by `pnpm gen:paper-cutouts`), with the paper's lit
 * rim + cast shadow. Floating pieces (candles…) bob gently on their own.
 *
 * Sized by `width` (px) or, without it, fills its container's width; height
 * follows the cut-out's canvas either way. Each piece carries its own filter so a
 * bobbing candle never forces the main piece to re-rasterise. `lite` keeps a
 * single, cheaper shadow layer for places that show many cut-outs at once (grids).
 */
import { computed } from 'vue';
import { getCutout, type CutoutName } from '@app/utils/paperCutouts';

/**
 * `still`: floating pieces stay put (e.g. paw prints) instead of bobbing like candles.
 * `lazy`: below-the-fold art — the images load lazily and decode off the main thread.
 */
const props = defineProps<{ name: CutoutName; width?: number; lite?: boolean; still?: boolean; lazy?: boolean }>();

const cutout = computed(() => getCutout(props.name));
const sizeStyle = computed(() =>
  props.width
    ? { width: `${props.width}px`, height: `${(props.width * cutout.value.height) / cutout.value.width}px` }
    : { width: '100%', aspectRatio: `${cutout.value.width} / ${cutout.value.height}` },
);
const pct = (v: number, of: number) => `${(v / of) * 100}%`;
</script>

<template>
  <div
    :class="['paper-cutout', lite && 'is-lite']"
    :style="sizeStyle"
  >
    <img
      v-for="(part, i) in cutout.parts"
      :key="part.url"
      :src="part.url"
      alt=""
      draggable="false"
      :loading="lazy ? 'lazy' : undefined"
      :decoding="lazy ? 'async' : undefined"
      :class="['pc-part', !part.main && !still && 'pc-float']"
      :style="{
        left: pct(part.x, cutout.width),
        top: pct(part.y, cutout.height),
        width: pct(part.w, cutout.width),
        animationDuration: `${2.6 + ((i * 0.37) % 1.4)}s`,
        animationDelay: `${-((i * 0.83) % 3)}s`,
      }"
    >
  </div>
</template>

<style scoped>
.paper-cutout {
  position: relative;
}
.pc-part {
  position: absolute;
  display: block;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
  filter: drop-shadow(0 -1px 0 var(--paper-edge, rgba(255, 245, 225, 0.35))) drop-shadow(0 6px 7px var(--paper-shade, rgba(4, 3, 18, 0.55)));
}
.is-lite .pc-part {
  filter: drop-shadow(0 4px 4px var(--paper-shade, rgba(4, 3, 18, 0.55)));
}
.pc-float {
  will-change: transform;
  animation: pc-bob 3s ease-in-out infinite alternate;
}
@keyframes pc-bob {
  from { transform: translateY(-3px) rotate(-2deg); }
  to { transform: translateY(3px) rotate(2deg); }
}
@media (prefers-reduced-motion: reduce) {
  .pc-float {
    animation: none;
  }
}
</style>
